/**
 * Vendored from https://github.com/persona-id/react-places-autocomplete
 * which is unmaintained
 *
 * NOTE: this requires @types/google.maps which exports a global namespace
 */

import { debounce } from 'lodash';
import {
  ChangeEvent,
  ChangeEventHandler,
  Component,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  TouchEventHandler,
} from 'react';

// transform snake_case to camelCase
const formattedSuggestion = (structuredFormatting: {
  main_text: string;
  secondary_text: string;
}) => ({
  mainText: structuredFormatting.main_text,
  secondaryText: structuredFormatting.secondary_text,
});

// Vendored functional programming utils that are hard to type
/* eslint-disable @typescript-eslint/no-explicit-any */
const compose =
  (...fns: any) =>
  (...args: any) => {
    fns.forEach((fn: any) => fn && fn(...args));
  };
/* eslint-enable @typescript-eslint/no-explicit-any */

type AutocompletePrediction = google.maps.places.AutocompletePrediction;

interface Suggestion {
  active: boolean;
  index: number;
  description: AutocompletePrediction['description'];
  placeId: AutocompletePrediction['place_id'];
  formattedSuggestion: {
    mainText: AutocompletePrediction['structured_formatting']['main_text'];
    secondaryText: AutocompletePrediction['structured_formatting']['secondary_text'];
  };
  matchedSubstrings: AutocompletePrediction['matched_substrings'];
  terms: AutocompletePrediction['terms'];
  types: AutocompletePrediction['types'];
}

interface GooglePlacesAutocompleteProps {
  onChange?: (value: string | null | undefined) => void;
  value?: string;
  onError?: (status: google.maps.places.PlacesServiceStatus, clearSuggestion: () => void) => void;
  onSelect?: (
    address: string | null | undefined,
    placeID: string | null | undefined,
    suggestion: Suggestion | null | undefined,
  ) => void;
  searchOptions?: {
    bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
    componentRestrictions?: google.maps.places.ComponentRestrictions;
    location?: google.maps.LatLng;
    offset?: number;
    radius?: number;
    sessionToken?: google.maps.places.AutocompleteSessionToken;
    types?: string[];
  };
  debounce?: number;
  highlightFirstSuggestion?: boolean;
  shouldFetchSuggestions?: boolean;
  googleCallbackName?: string;
  children: (
    opts: Readonly<{
      loading: boolean;
      suggestions: ReadonlyArray<Suggestion>;
      getInputProps: <
        InputProps extends Partial<{
          type: string;
          autoComplete: string;
          role: string;
          'aria-autocomplete': string;
          'aria-expanded': boolean;
          'aria-activedescendant': string | undefined;
          disabled: boolean;
          onKeyDown: KeyboardEventHandler;
          onBlur: FocusEventHandler;
          onFocus: FocusEventHandler;
          value: string | undefined;
          onChange: ChangeEventHandler;
        }>,
      >(
        options?: InputProps,
      ) => {
        type: string;
        autoComplete: string;
        role: string;
        'aria-autocomplete': string;
        'aria-expanded': boolean;
        'aria-activedescendant': string | undefined;
        disabled: boolean;
        onKeyDown: KeyboardEventHandler;
        onBlur: FocusEventHandler;
        onFocus: FocusEventHandler;
        value: string | undefined;
        onChange: ChangeEventHandler;
      };
      getSuggestionItemProps: <
        SuggestionProps extends Partial<{
          key: string;
          id: string | undefined;
          role: 'option';
          onMouseEnter: MouseEventHandler;
          onMouseLeave: MouseEventHandler;
          onMouseDown: MouseEventHandler;
          onMouseUp: MouseEventHandler;
          onTouchStart: TouchEventHandler;
          onTouchEnd: TouchEventHandler;
          onClick: MouseEventHandler;
          active: boolean;
        }>,
      >(
        suggestion: Suggestion,
        options?: SuggestionProps,
      ) => {
        key: string;
        id: string | undefined;
        role: 'option';
        onMouseEnter: MouseEventHandler;
        onMouseLeave: MouseEventHandler;
        onMouseDown: MouseEventHandler;
        onMouseUp: MouseEventHandler;
        onTouchStart: TouchEventHandler;
        onTouchEnd: TouchEventHandler;
        onClick: MouseEventHandler;
        active?: boolean;
      };
    }>,
  ) => ReactNode;
}

interface GooglePlacesAutocompleteState {
  loading: boolean;
  suggestions: ReadonlyArray<Suggestion>;
  userInputValue: string | undefined;
  ready: boolean;
  hideSuggestions: boolean;
}

class GooglePlacesAutocomplete extends Component<
  GooglePlacesAutocompleteProps,
  GooglePlacesAutocompleteState
> {
  static defaultProps: Partial<GooglePlacesAutocompleteProps> = {
    onError: (status, _clearSuggestions) =>
      console.error(
        '[react-places-autocomplete]: error happened when fetching data from Google Maps API.\nPlease check the docs here (https://developers.google.com/maps/documentation/javascript/places#place_details_responses)\nStatus: ',
        status,
      ),
    searchOptions: {},
    debounce: 200,
    highlightFirstSuggestion: false,
    shouldFetchSuggestions: true,
  };

  debouncedFetchPredictions: () => void;
  autocompleteService: google.maps.places.AutocompleteService | undefined;
  autocompleteOK: google.maps.places.PlacesServiceStatus | undefined;
  mousedownOnSuggestion: boolean | undefined;

  constructor(props: GooglePlacesAutocompleteProps) {
    super(props);

    this.state = {
      loading: false,
      suggestions: [],
      userInputValue: props.value,
      ready: !props.googleCallbackName,
      hideSuggestions: true,
    };

    this.debouncedFetchPredictions = debounce(this.fetchPredictions, props.debounce);
  }

  componentDidMount() {
    const { googleCallbackName } = this.props;
    if (googleCallbackName) {
      const isPlacesLoaded = window.google && window.google.maps && window.google.maps.places;
      if (!isPlacesLoaded) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any)[googleCallbackName] = this.init;
      } else {
        this.init();
      }
    } else {
      this.init();
    }
  }

  componentWillUnmount() {
    const { googleCallbackName } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (googleCallbackName && (window as any)[googleCallbackName]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (window as any)[googleCallbackName];
    }
  }

  /**
   * Don't update if just changing userInputValue, this makes the module compatible with Redux.
   */
  shouldComponentUpdate(
    nextProps: GooglePlacesAutocompleteProps,
    nextState: GooglePlacesAutocompleteState,
  ) {
    let changed = false;
    const propKeys = Object.keys(this.props) as Array<keyof GooglePlacesAutocompleteProps>;
    propKeys.forEach((key) => {
      if (this.props[key] !== nextProps[key]) {
        changed = true;
      }
    });
    const stateKeys = Object.keys(this.state) as Array<keyof GooglePlacesAutocompleteState>;
    stateKeys.forEach((key) => {
      if (this.state[key] !== nextState[key] && key !== 'userInputValue') {
        changed = true;
      }
    });
    return changed;
  }

  init = () => {
    if (!window.google) {
      throw new Error(
        '[react-places-autocomplete]: Google Maps JavaScript API library must be loaded. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library',
      );
    }

    if (!window.google.maps.places) {
      throw new Error(
        '[react-places-autocomplete]: Google Maps Places library must be loaded. Please add `libraries=places` to the src URL. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library',
      );
    }

    this.autocompleteService = new window.google.maps.places.AutocompleteService();
    this.autocompleteOK = window.google.maps.places.PlacesServiceStatus.OK;
    this.setState((state) => {
      if (state.ready) {
        return null;
      } else {
        return { ready: true };
      }
    });
  };

  autocompleteCallback = (
    predictions: AutocompletePrediction[] | null,
    status: google.maps.places.PlacesServiceStatus,
  ) => {
    this.setState({ loading: false });
    if (status !== this.autocompleteOK) {
      this.props.onError?.(status, this.clearSuggestions);
      return;
    }
    const { highlightFirstSuggestion } = this.props;
    this.setState({
      suggestions:
        predictions?.map((p, idx) => ({
          description: p.description,
          placeId: p.place_id,
          active: highlightFirstSuggestion && idx === 0 ? true : false,
          index: idx,
          formattedSuggestion: formattedSuggestion(p.structured_formatting),
          matchedSubstrings: p.matched_substrings,
          terms: p.terms,
          types: p.types,
        })) ?? [],
      hideSuggestions: false,
    });
  };

  fetchPredictions = () => {
    const { value } = this.props;
    if (value?.length) {
      this.setState({ loading: true });
      this.autocompleteService?.getPlacePredictions(
        {
          ...this.props.searchOptions,
          input: value,
        },
        this.autocompleteCallback,
      );
    }
  };

  clearSuggestions = () => {
    this.setState({ suggestions: [] });
  };

  clearActive = () => {
    this.setState({
      suggestions: this.state.suggestions.map((suggestion) => ({
        ...suggestion,
        active: false,
      })),
    });
  };

  hideSuggestions = () => {
    this.setState({
      ...this.state,
      hideSuggestions: true,
    });
  };

  handleSelect = (
    address: string | null | undefined,
    placeId: string | null | undefined,
    suggestion: Suggestion | null | undefined,
  ) => {
    this.hideSuggestions();
    if (this.props.onSelect) {
      this.props.onSelect(address, placeId, suggestion);
    } else {
      this.props.onChange?.(address);
    }
  };

  getActiveSuggestion = () => {
    return this.state.suggestions.find((suggestion) => suggestion.active);
  };

  selectActiveAtIndex = (index: number) => {
    const activeName = this.state.suggestions.find(
      (suggestion) => suggestion.index === index,
    )?.description;
    this.setActiveAtIndex(index);
    this.props.onChange?.(activeName);
  };

  selectUserInputValue = () => {
    this.clearActive();
    this.props.onChange?.(this.state.userInputValue);
  };

  handleEnterKey = () => {
    const activeSuggestion = this.getActiveSuggestion();
    if (activeSuggestion == null) {
      this.handleSelect(this.props.value, null, null);
    } else {
      this.handleSelect(activeSuggestion.description, activeSuggestion.placeId, activeSuggestion);
    }
  };

  handleDownKey = () => {
    if (this.state.suggestions.length === 0) {
      return;
    }

    const activeSuggestion = this.getActiveSuggestion();
    if (activeSuggestion == null) {
      this.selectActiveAtIndex(0);
    } else if (activeSuggestion.index === this.state.suggestions.length - 1) {
      this.selectUserInputValue();
    } else {
      this.selectActiveAtIndex(activeSuggestion.index + 1);
    }
  };

  handleUpKey = () => {
    if (this.state.suggestions.length === 0) {
      return;
    }

    const activeSuggestion = this.getActiveSuggestion();
    if (activeSuggestion == null) {
      this.selectActiveAtIndex(this.state.suggestions.length - 1);
    } else if (activeSuggestion.index === 0) {
      this.selectUserInputValue();
    } else {
      this.selectActiveAtIndex(activeSuggestion.index - 1);
    }
  };

  handleInputKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.handleEnterKey();
        break;
      case 'ArrowDown':
        event.preventDefault(); // prevent the cursor from moving
        this.handleDownKey();
        break;
      case 'ArrowUp':
        event.preventDefault(); // prevent the cursor from moving
        this.handleUpKey();
        break;
      case 'Escape':
        this.hideSuggestions();
        break;
    }
    /* eslint-enable indent */
  };

  setActiveAtIndex = (index: number) => {
    this.setState({
      suggestions: this.state.suggestions.map((suggestion, idx) => {
        if (idx === index) {
          return { ...suggestion, active: true };
        } else {
          return { ...suggestion, active: false };
        }
      }),
    });
  };

  handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    this.props.onChange?.(value);
    this.setState({ userInputValue: value });
    if (!value) {
      this.clearSuggestions();
      return;
    }
    if (this.props.shouldFetchSuggestions) {
      this.debouncedFetchPredictions();
    }
  };

  handleInputOnBlur = () => {
    if (!this.mousedownOnSuggestion) {
      this.hideSuggestions();
    }
  };

  handleInputOnFocus = () => {
    this.setState({
      ...this.state,
      hideSuggestions: false,
    });
  };

  getActiveSuggestionId = () => {
    const activeSuggestion = this.getActiveSuggestion();
    return activeSuggestion
      ? `PlacesAutocomplete__suggestion-${activeSuggestion.placeId}`
      : undefined;
  };

  getIsExpanded = () => {
    return this.state.suggestions.length > 0;
  };

  getInputProps: Parameters<GooglePlacesAutocompleteProps['children']>[0]['getInputProps'] = (
    options,
  ) => {
    const defaultInputProps = {
      type: 'text',
      autoComplete: 'off',
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-expanded': this.getIsExpanded(),
      'aria-activedescendant': this.getActiveSuggestionId(),
      disabled: !this.state.ready,
    };

    return {
      ...defaultInputProps,
      ...options,
      onKeyDown: compose(this.handleInputKeyDown, options?.onKeyDown),
      onBlur: compose(this.handleInputOnBlur, options?.onBlur),
      onFocus: compose(this.handleInputOnFocus, options?.onFocus),
      value: this.props.value,
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        this.handleInputChange(event);
      },
    };
  };

  getSuggestionItemProps: Parameters<
    GooglePlacesAutocompleteProps['children']
  >[0]['getSuggestionItemProps'] = (
    suggestion: Suggestion,
    options: Partial<
      ReturnType<Parameters<GooglePlacesAutocompleteProps['children']>[0]['getSuggestionItemProps']>
    > = {},
  ) => {
    const handleSuggestionMouseEnter = this.handleSuggestionMouseEnter.bind(this, suggestion.index);
    const handleSuggestionClick = this.handleSuggestionClick.bind(this, suggestion);

    return {
      ...options,
      key: suggestion.placeId,
      id: this.getActiveSuggestionId(),
      role: 'option',
      onMouseEnter: compose(handleSuggestionMouseEnter, options.onMouseEnter),
      onMouseLeave: compose(this.handleSuggestionMouseLeave, options.onMouseLeave),
      onMouseDown: compose(this.handleSuggestionMouseDown, options.onMouseDown),
      onMouseUp: compose(this.handleSuggestionMouseUp, options.onMouseUp),
      onTouchStart: compose(this.handleSuggestionTouchStart, options.onTouchStart),
      onTouchEnd: compose(this.handleSuggestionMouseUp, options.onTouchEnd),
      onClick: compose(handleSuggestionClick, options.onClick),
    };
  };

  handleSuggestionMouseEnter = (index: number) => {
    this.setActiveAtIndex(index);
  };

  handleSuggestionMouseLeave = () => {
    this.mousedownOnSuggestion = false;
    this.clearActive();
  };

  handleSuggestionMouseDown = (event: MouseEvent) => {
    event.preventDefault();
    this.mousedownOnSuggestion = true;
  };

  handleSuggestionTouchStart = () => {
    this.mousedownOnSuggestion = true;
  };

  handleSuggestionMouseUp = () => {
    this.mousedownOnSuggestion = false;
  };

  handleSuggestionClick = (suggestion: Suggestion, event: MouseEvent) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    const { description, placeId } = suggestion;
    this.handleSelect(description, placeId, suggestion);
    setTimeout(() => {
      this.mousedownOnSuggestion = false;
    });
  };

  render() {
    return this.props.children({
      getInputProps: this.getInputProps,
      getSuggestionItemProps: this.getSuggestionItemProps,
      loading: this.state.loading,
      suggestions: this.state.hideSuggestions ? [] : this.state.suggestions,
    });
  }
}

export default GooglePlacesAutocomplete;

export const geocodeByAddress = (address: string): Promise<google.maps.GeocoderResult[]> => {
  const geocoder = new window.google.maps.Geocoder();
  const OK = window.google.maps.GeocoderStatus.OK;

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status !== OK || results == null) {
        reject(status);
      } else {
        resolve(results);
      }
    });
  });
};

export const getLatLng = (
  result: google.maps.GeocoderResult,
): Promise<google.maps.LatLngLiteral> => {
  return new Promise((resolve, reject) => {
    try {
      const latLng = {
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng(),
      };
      resolve(latLng);
    } catch (e) {
      reject(e);
    }
  });
};

export const geocodeByPlaceId = (
  placeId: string | undefined,
): Promise<google.maps.GeocoderResult[]> => {
  const geocoder = new window.google.maps.Geocoder();
  const OK = window.google.maps.GeocoderStatus.OK;

  return new Promise((resolve, reject) => {
    geocoder.geocode({ placeId }, (results, status) => {
      if (status !== OK || results == null) {
        reject(status);
      } else {
        resolve(results);
      }
    });
  });
};
